import React, { useMemo } from "react";
import { useTranslation } from "react-i18next"; // Keep for 't' function
import VariationSelector from "../../common/VariationSelector"; // Import the new component
import DetailsActions from "@/js/features/cart/components/addToCarts/DetailsActions";
import ProductInformation from "./ProductInformation";
import ProductMeta from "./ProductMeta";
import type {
  TransformedProduct,
  TransformedVariation,
} from "../../../types/productApiTypes";
import type { SelectedAttributeSlugs } from "../../../hooks/useProductVariationSelector";

// --- Component Props ---
interface ProductDetailsProps {
  // Main required prop
  product: TransformedProduct;
  // Variation related props
  variations?: TransformedVariation[]; // Still useful for potentially listing all options if needed
  variationsLoading?: boolean; // This prop now includes translation readiness from parent
  selectedVariation: TransformedVariation | null;
  selectedAttributes: SelectedAttributeSlugs; // Expects { 'pa_color': 'blue', ... }
  onAttributeSelect: (attributeName: string, valueSlug: string) => void;
  // Removed productCategories & productTags props
}

// --- Component Implementation ---
const ProductDetails: React.FC<ProductDetailsProps> = ({
  product,
  variationsLoading = false, // This prop now includes translation readiness from parent
  selectedVariation = null,
  selectedAttributes = {},
  onAttributeSelect,
}) => {
  const { t } = useTranslation(); // Keep for 't' function

  // --- Extract Data from Product Prop ---
  const {
    name: productName,
    shortDescription,
    enrichedCategories = [], // Default to empty array
    enrichedTags = [], // Default to empty array
    enrichedBrands = [], // Extract brands, default to empty array
    enrichedAttributes = [], // Default to empty array
    type: productType,
    sku: productSku,
    formattedPrice,
    formattedRegularPrice,
    formattedSalePrice,
    onSale: productOnSale,
    stockStatus: productStockStatus,
    stockQuantity: productStockQuantity, // Extract product stock quantity
  } = product;

  // --- Constants & Derived Values ---
  const currencySymbol = window.themeVariables?.currencySymbol || "₺";
  const isVariable = productType === "variable";
  const siteUrl = window.themeVariables?.siteUrl || "";
  const shopUrl = `${siteUrl}/shop/`;

  // Use the loading state passed from the parent directly
  const isLoading = variationsLoading;

  // --- Calculated States ---
  const hasRequiredAttributes = useMemo(() => {
    if (!isVariable) return true;
    return enrichedAttributes
      .filter((attr) => attr.hasVariations && attr.taxonomy)
      .every((attr) => !!selectedAttributes[attr.taxonomy!]);
  }, [isVariable, enrichedAttributes, selectedAttributes]);

  const isCombinationAvailable = !!selectedVariation;

  const getStockStatus = (): TransformedProduct["stockStatus"] => {
    if (isVariable && selectedVariation) return selectedVariation.stockStatus;
    return productStockStatus;
  };
  const stockStatus = getStockStatus();

  // --- NEW: Get current stock quantity ---
  const getCurrentStockQuantity = (): number | null | undefined => {
    if (isVariable && selectedVariation) {
      return selectedVariation.stockQuantity; // Use variation quantity
    }
    return productStockQuantity; // Use product quantity
  };
  const currentStockQuantity = getCurrentStockQuantity();
  // --- END NEW ---

  const shouldDisplayPrice = (): boolean => {
    if (!isVariable) return true;
    return isCombinationAvailable && !!selectedVariation?.formattedPrice;
  };

  const getPriceData = () => {
    if (
      isVariable &&
      isCombinationAvailable &&
      selectedVariation?.formattedPrice
    ) {
      return {
        formattedPrice: selectedVariation.formattedPrice,
        formattedRegularPrice: selectedVariation.formattedRegularPrice || "", // Provide default empty string
        formattedSalePrice: selectedVariation.formattedSalePrice || null, // Keep null if undefined/empty
        onSale: selectedVariation.onSale,
      };
    }
    return {
      formattedPrice,
      formattedRegularPrice,
      formattedSalePrice,
      onSale: productOnSale,
    };
  };
  const priceData = getPriceData();

  const getSku = (): string => {
    if (isVariable && isCombinationAvailable && selectedVariation?.sku) {
      return selectedVariation.sku;
    }
    return productSku;
  };
  const currentSku = getSku(); // Get the current SKU

  return (
    <div className="product-details">
      {/* Product Information */}
      <ProductInformation
        productName={productName}
        priceData={priceData}
        stockStatus={stockStatus}
        shortDescription={shortDescription}
        variationsLoading={isLoading} // Pass combined loading state down
        shouldDisplayPrice={shouldDisplayPrice()}
        isVariable={isVariable}
        currencySymbol={currencySymbol}
        isCombinationAvailable={isCombinationAvailable}
      />

      {/* Variations Section - Replaced with VariationSelector */}
      {isVariable && (
        <VariationSelector
          enrichedAttributes={enrichedAttributes}
          selectedAttributes={selectedAttributes}
          onAttributeSelect={onAttributeSelect}
          variationsLoading={isLoading} // Pass combined loading state down
          containerClassName="variations" // Pass specific class for ProductDetails context
          rowClassName="variation-row" // Pass specific class for ProductDetails context
        />
      )}

      {/* Variation Notices */}
      {isVariable && !isLoading && !hasRequiredAttributes && (
        <div className="variation-notice">
          {t("product.pleaseSelectOptions")} {/* Translate */}
        </div>
      )}
      {isVariable &&
        hasRequiredAttributes &&
        !isLoading &&
        !isCombinationAvailable && (
          <div className="variation-required-notice">
            {t("product.combinationUnavailable")} {/* Translate */}
          </div>
        )}

      {/* Add to Cart Form - Use the REFACTORED DetailsActions */}
      {/* Pass showQuantitySelector={true} (default) and showViewDetailsButton={false} (default) */}
      <DetailsActions
        product={product}
        selectedVariation={selectedVariation}
        isVariable={isVariable}
        isCombinationAvailable={isCombinationAvailable}
        hasRequiredAttributes={hasRequiredAttributes}
        variationsLoading={isLoading}
        stockStatus={stockStatus}
        stockQuantity={currentStockQuantity}
        selectedAttributes={selectedAttributes}
        // showQuantitySelector={true} // No need to pass if true is default
        // showViewDetailsButton={false} // No need to pass if false is default
        containerClassName="cart-form single-product-actions" // Optional: Add specific class if needed
      />

      {/* Use the new ProductMeta component // COMMENTED OUT */}
      {/*       <ProductMeta
        sku={currentSku}
        categories={enrichedCategories}
        tags={enrichedTags}
        brands={enrichedBrands}
        shopUrl={shopUrl}
      /> */}
    </div>
  );
};

export default ProductDetails;
